// Canonical release blog document for Wild Things.
import { defineDocument } from '../../schema';
export const blogWildThingsWantedDocument = defineDocument({
    id: 'blog:wild-things-wanted',
    kind: 'blog',
    slug: 'wild-things-wanted',
    route: '/blog/wild-things-wanted',
    seo: {
        title: 'Wild Things Wanted',
        description: 'Airhead Games launches real-time drawing engine and our first two drawing games.',
        imageAsset: 'coverWildThings',
    },
    hero: {
        eyebrow: [
            {
                type: 'text',
                text: 'Airhead Blog',
            },
        ],
        title: [
            {
                type: 'text',
                text: 'Wild Things Wanted',
            },
        ],
        description: [
            {
                type: 'text',
                text: 'Airhead Games launches real-time drawing engine and our first two drawing games.',
            },
        ],
        imageAsset: 'coverWildThings',
        imageAlt: 'Wild Things cover art',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-07-11T00:00:00.000Z'),
        updatedAt: new Date('2026-07-11T00:00:00.000Z'),
        tags: ['games'],
    },
    sections: [
        {
            id: 'article',
            blocks: [
                {
                    type: 'paragraph',
                    content: [
                        {
                            type: 'text',
                            text: `Any good party pack needs a drawing game, so today we're launching our first two. `,
                        },
                        {
                            type: 'link',
                            href: '/discover/games/wild-things',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Wild Things',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' and ',
                        },
                        {
                            type: 'link',
                            href: '/discover/games/wanted',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Wanted',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: ' are both available in the ',
                        },
                        {
                            type: 'link',
                            href: '/library',
                            children: [
                                {
                                    type: 'text',
                                    text: 'Game Library',
                                },
                            ],
                        },
                        {
                            type: 'text',
                            text: '.',
                        },
                    ],
                },
            ],
            sections: [
                {
                    id: 'article__the-canvas-awaits',
                    title: [
                        {
                            type: 'text',
                            text: 'The Canvas Awaits',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'In June we put a bare drawing board at ',
                                },
                                {
                                    type: 'link',
                                    href: '/draw',
                                    children: [
                                        {
                                            type: 'text',
                                            text: '/draw',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' and invited the community to test the tools and send me an image. People drew and reported the rough spots. Version two of the tools came shortly after based on the feedback. The toolbar covers colors, brush size, opacity, zoom, undo, and redo, and it all works on any device.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `Today the canvas gets its first two games. More than a drawing canvas, it's a real time mirror into an artist's masterpiece. As every stroke lands, it can also appear on other players' screens or the host's screen. Wanted pairs this with a fastest-to-guess mechanic and Wild Things uses the mirror to help players connect their collaborative drawings together.`,
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'The original invitation stands. ',
                                },
                                {
                                    type: 'link',
                                    href: '/draw',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Sketch something',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ` and send it my way. If you have ideas to further improve the drawing tools, we'd love to hear from you in `,
                                },
                                {
                                    type: 'link',
                                    href: 'https://discord.gg/rfFTVAqHwH',
                                    children: [
                                        {
                                            type: 'text',
                                            text: "Poppy's Pals Discord",
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: `. Your input helps us sharpen the pencils!`,
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__discover-new-creatures-in-wild-things',
                    title: [
                        {
                            type: 'text',
                            text: 'Discover New Creatures in Wild Things',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: `Wild Things is "exquisite corpse" meets nature documentary. Players collaborate by each drawing a segment of a strange, hybrid creature. The results are as hilarious and ridiculous as you might expect. There are no winners, just the fun of the group's collective creativity and disastrous mishaps on display.`,
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Up to 16 players can join this game. The room starts by deciding what was spotted moving in the brush, then two things fuse into one, and groups split out to sketch the specimen. The live mirror shows what your fellow naturalists are drawing on either side of you, helping you line up your own portion of the beast. After the drawings are finished, the room names its discovery and a real photo is added to the nature journal.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'I knew this game would work before it was finished: the first hybrid animal generated - part horse and part fish - absolutely blew me away. Launch day really brought it to life though. Streamer ',
                                },
                                {
                                    type: 'link',
                                    href: 'https://www.twitch.tv/edgywizard',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Edgywizard',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' and his crew were the first to jump in. They spotted a shark-crow on their first turn, with both expedition teams sketching their own take on it. Hilariously, ',
                                },
                                {
                                    type: 'em',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'bartthebear69',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' misread "crow" and added a portion of a "cow" to his expedition\'s sketch, making the strange creature even funnier. The bizarre new species, ultimately named Dave, became the first new discovery in the Airhead Games nature journal.',
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'blogWildThingsDave',
                            imageAlt: 'Player-drawn composite sketch of Dave: a shark head in a top hat, a cow-like middle, and a crow tail',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'Dave, as documented by the expedition: part shark, part crow, and apparently part cow.',
                                },
                            ],
                            maxWidth: 760,
                        },
                    ],
                },
                {
                    id: 'article__your-artwork-isnt-the-only-crime-in-wanted',
                    title: [
                        {
                            type: 'text',
                            text: "Your Artwork Isn't The Only Crime in Wanted",
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Wanted is our tribute to the classic draw-and-guess formula. The twist that made it ours: the drawing as evidence. Every round pairs a suspect with an absurd crime, with the Sketch Artist drawing the suspect caught in the act on a WANTED poster.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: "As the artist draws, the live mirror relays every stroke onto the host's screen and everyone else races to identify the suspect. Correctly guessing the suspect before the other detectives will lock in a higher score for the guesser and the artist. Once the suspect is unmasked, detectives accuse the suspect of a crime from a lineup of decoys. A bonus is earned for the artist and each detective who correctly sniffs out the crime.",
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'One of the cases worth framing from the opening weekend came from a home game, where artist Sarah put a ',
                                },
                                {
                                    type: 'strong',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Mermaid',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' on the poster, clearly committing the sin of loitering while wearing too many hats.',
                                },
                            ],
                        },
                        {
                            type: 'image',
                            imageAsset: 'blogWantedMermaidPoster',
                            imageAlt: 'Player-drawn WANTED poster sketch of a mermaid balancing a tall stack of colorful hats',
                            caption: [
                                {
                                    type: 'text',
                                    text: 'Exhibit A: entered into evidence by sketch artist Sarah.',
                                },
                            ],
                            maxWidth: 420,
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'For me, the game really came together when the audio landed: spaghetti-western stings on every reveal, and songs like ',
                                },
                                {
                                    type: 'link',
                                    href: '/discover/music?song=Alibi',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'Alibi',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' set the silly standoff mood.',
                                },
                            ],
                        },
                    ],
                },
                {
                    id: 'article__ready-to-make-your-mark',
                    title: [
                        {
                            type: 'text',
                            text: 'Ready to Make Your Mark?',
                        },
                    ],
                    blocks: [
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Wild Things and Wanted are both live in ',
                                },
                                {
                                    type: 'link',
                                    href: '/library',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'the library',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' right now. Each game provides a unique way to bring your community together.',
                                },
                            ],
                        },
                        {
                            type: 'paragraph',
                            content: [
                                {
                                    type: 'text',
                                    text: 'Just feel like doodling? The ',
                                },
                                {
                                    type: 'link',
                                    href: '/draw',
                                    children: [
                                        {
                                            type: 'text',
                                            text: 'drawing sandbox',
                                        },
                                    ],
                                },
                                {
                                    type: 'text',
                                    text: ' is open anytime, no game or group required.',
                                },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: 'article-actions',
            widgets: [
                {
                    type: 'relatedDocuments',
                    limit: 4,
                },
            ],
        },
    ],
});
//# sourceMappingURL=wild-things-wanted.js.map